import { DatePickerTR } from "@/components/DatePickerTR";
import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Archive, Loader2, Save, Github, Play, ShieldCheck, Clock, FileText, CheckCircle2, RotateCcw, Printer, Hash, Search, Link2, Trash2, CheckSquare, Square } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";
import { printBlankTemplate } from "@/lib/templates";
import { DOC_CATEGORIES } from "@/components/DocumentManager";
import { fileUrl } from "@/components/EntityAvatar";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return iso; } };

const STATUS_META = {
  active: { label: "Aktif", cls: "bg-slate-100 text-slate-600" },
  protected: { label: "Korumalı", cls: "bg-sky-100 text-sky-700" },
  expiring: { label: "Süresi Doldu", cls: "bg-amber-100 text-amber-700" },
  archive_ready: { label: "Arşive Hazır", cls: "bg-orange-100 text-orange-700" },
  archived: { label: "Arşivlendi", cls: "bg-emerald-100 text-emerald-700" },
  held: { label: "Elde Tutuldu", cls: "bg-indigo-100 text-indigo-700" },
};
const FILTERS = [["all", "Tümü"], ["active", "Aktif"], ["protected", "Korumalı"], ["expiring", "Süresi Doldu"], ["archive_ready", "Arşive Hazır"], ["archived", "Arşivlendi"], ["held", "Elde Tutuldu"]];

const fmtSize = (b) => (!b ? "—" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export default function DocumentArchive() {
  const [cfg, setCfg] = useState(null);
  const [docs, setDocs] = useState({ items: [], counts: {}, retention_months: 120 });
  const [status, setStatus] = useState("all");
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [ghTesting, setGhTesting] = useState(false);
  const [docNums, setDocNums] = useState({ items: [], counts: {} });
  const [dnQ, setDnQ] = useState("");
  const [dnStatus, setDnStatus] = useState("all");
  const [dnFrom, setDnFrom] = useState("");
  const [dnTo, setDnTo] = useState("");
  const dnPaged = usePaged(docNums.items, 10);
  const docsPaged = usePaged(docs.items, 10);
  const [esQ, setEsQ] = useState("");
  const [esCat, setEsCat] = useState("");
  const [esType, setEsType] = useState("");
  const [esData, setEsData] = useState({ items: [], count: 0 });
  const [esLoading, setEsLoading] = useState(false);
  const loadEntitySearch = useCallback(() => {
    setEsLoading(true);
    api.get("/documents/search", { params: { q: esQ, category: esCat, entity_type: esType } })
      .then((r) => setEsData(r.data)).catch(() => setEsData({ items: [], count: 0 })).finally(() => setEsLoading(false));
  }, [esQ, esCat, esType]);

  const loadCfg = useCallback(() => api.get("/settings/doc-retention").then((r) => setCfg(r.data)).catch(() => {}), []);
  const loadDocs = useCallback(() => api.get(`/documents/archive?status=${status}`).then((r) => setDocs(r.data)).catch(() => {}), [status]);
  const loadDocNums = useCallback(() => api.get(`/documents/doc-numbers?q=${encodeURIComponent(dnQ)}&status=${dnStatus}&date_from=${dnFrom}&date_to=${dnTo}`).then((r) => setDocNums(r.data)).catch(() => {}), [dnQ, dnStatus, dnFrom, dnTo]);

  const testGithub = async () => {
    setGhTesting(true);
    try {
      const { data } = await api.post("/documents/github-test");
      toast.success(`GitHub bağlantısı OK: ${data.repo} (${data.private ? "özel" : "genel"}) · yazma yetkisi var`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setGhTesting(false); }
  };

  const restoreDoc = async (id) => {
    try {
      await api.post(`/documents/${id}/restore`);
      toast.success("Belge sisteme geri getirildi (arşivden çıkarıldı, tekrar arşivlenmeyecek).");
      loadDocs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pageIds = docsPaged.slice.map((d) => d.id);
  const allPageSel = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const togglePage = () => setSelected((s) => { const n = new Set(s); allPageSel ? pageIds.forEach((id) => n.delete(id)) : pageIds.forEach((id) => n.add(id)); return n; });

  const delOne = async (id) => {
    if (!window.confirm("Bu belge kalıcı olarak silinsin mi?")) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/documents/${id}`);
      toast.success(data.pending_approval ? "Silme isteği yöneticinin onayına gönderildi." : "Belge silindi.");
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; }); loadDocs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setDeleting(false); }
  };
  const delBulk = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Seçili ${ids.length} belge kalıcı olarak silinsin mi?`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/documents/bulk-delete", { ids });
      toast.success(data.pending_approval ? `${ids.length} belge için silme isteği yöneticinin onayına gönderildi.` : `${data.deleted} belge silindi.`);
      setSelected(new Set()); loadDocs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setDeleting(false); }
  };

  useEffect(() => { loadCfg(); }, [loadCfg]);
  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { const t = setTimeout(loadDocNums, 250); return () => clearTimeout(t); }, [loadDocNums]);
  useEffect(() => { const t = setTimeout(loadEntitySearch, 300); return () => clearTimeout(t); }, [loadEntitySearch]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { months: Number(cfg.months) || 120, security_days: Number(cfg.security_days) || 180, github_enabled: cfg.github.enabled, github_repo: cfg.github.repo, github_branch: cfg.github.branch, github_token: cfg.github.token };
      const { data } = await api.put("/settings/doc-retention", body);
      setCfg(data); toast.success("Saklama ayarı kaydedildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/documents/retention-run");
      toast.success(`Tarama tamam: ${data.flagged} belge arşive hazır${data.github_enabled ? `, ${data.archived} GitHub'a yüklendi` : ""}.`);
      loadDocs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setRunning(false); }
  };

  const c = docs.counts || {};

  return (
    <div className="mx-auto max-w-5xl space-y-5 fade-in" data-testid="document-archive-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tight text-slate-900">
          <Archive className="h-6 w-6 text-brand-green" /> Belge Arşivi
        </h1>
        <p className="mt-1 text-sm text-slate-500">Yüklenen belgeler yasal gereklilik uyarınca <b>10 yıl (varsayılan {docs.retention_months} ay)</b> sistemde tutulur, sonra arşive alınır. Araç ruhsat/sigorta, personel özlük gibi <b>varlığa bağlı belgeler</b> ilgili araç/şoför/personel silinene kadar korunur; varlık silinince ilgili belge de <b>sistemden silinir</b> (10 yıllık saklamaya tabi değildir).</p>
      </div>

      {/* Özet kartlar */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
        {[["active", FileText], ["protected", ShieldCheck], ["expiring", Clock], ["archive_ready", Archive], ["archived", CheckCircle2], ["held", RotateCcw]].map(([k, Icon]) => (
          <div key={k} data-testid={`doc-count-${k}`} className="rounded-xl border border-slate-200 bg-white p-4">
            <Icon className="mb-1 h-4 w-4 text-slate-400" />
            <p className="overline">{STATUS_META[k].label}</p>
            <p className="font-heading text-2xl font-bold text-slate-900">{c[k] || 0}</p>
          </div>
        ))}
      </div>

      {/* Boş Belge Şablonları */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="doc-templates-card">
        <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><FileText className="h-5 w-5 text-brand-green" /> Boş Belge Şablonları</p>
        <p className="mt-1 text-sm text-slate-500">Zorunlu belgeleri yazdırın, elle doldurup imzalayın ve ilgili işlemde (Satın Alma, İç İkmal, Transfer, Mal Kabul) sisteme geri yükleyin.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {[["purchase", "Satın Alma Sipariş Formu"], ["internal", "İç Tedarik / İç İkmal Formu"], ["transfer", "Transfer / Sevk Belgesi"], ["goods_receipt", "Mal Kabul Föyü"], ["count", "Depo Sayım Föyü"], ["personnel", "Personel Özlük Formu"], ["contacts", "Kişiler / Cari Bilgi Formu"]].map(([k, l]) => (
            <Button key={k} data-testid={`doc-template-${k}`} onClick={async () => { const no = await printBlankTemplate(k); if (no) toast.success(`Belge No üretildi: ${no}`); }} variant="outline" size="sm" className="border-slate-300">
              <Printer className="mr-1.5 h-4 w-4 text-brand-green" /> {l}
            </Button>
          ))}
        </div>
      </div>

      {/* Belge No Kayıtları */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="doc-numbers-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Hash className="h-5 w-5 text-brand-green" /> Belge No Kayıtları</p>
          <div className="flex items-center gap-2 text-xs">
            <span data-testid="docnum-count-reserved" className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">{docNums.counts?.reserved || 0} rezerve</span>
            <span data-testid="docnum-count-used" className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700">{docNums.counts?.used || 0} bağlandı</span>
          </div>
        </div>
        <p className="mt-1 text-sm text-slate-500">Üretilen/basılan belge numaraları burada listelenir. İşlem sisteme girildiğinde numara otomatik <b>bağlanır</b> (rezerve → bağlandı) ve aranabilir olur.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="docnum-search" value={dnQ} onChange={(e) => setDnQ(e.target.value)} placeholder="Belge no ara... (örn. MK-2026)" className="pl-9" />
          </div>
          {[["all", "Tümü"], ["reserved", "Rezerve"], ["used", "Bağlandı"]].map(([v, l]) => (
            <button key={v} data-testid={`docnum-filter-${v}`} onClick={() => setDnStatus(v)}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${dnStatus === v ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
          ))}
          <DatePickerTR testid="docnum-from" value={dnFrom} onChange={(v) => setDnFrom(v)} className="mt-0 h-9 w-40" />
          <span className="text-xs text-slate-400">–</span>
          <DatePickerTR testid="docnum-to" value={dnTo} onChange={(v) => setDnTo(v)} className="mt-0 h-9 w-40" />
        </div>
        <div className="mt-3 overflow-x-auto">
          {docNums.items.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Belge no kaydı yok.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase text-slate-400">
                <th className="py-2">Belge No</th><th>Tür</th><th>Durum</th><th>Bağlı İşlem</th><th>Oluşturan</th>
              </tr></thead>
              <tbody>
                {dnPaged.slice.map((d) => (
                  <tr key={d.id} data-testid={`docnum-row-${d.id}`} className="border-b border-slate-50">
                    <td className="py-2 font-mono text-[12px] font-semibold text-slate-800">{d.doc_no}</td>
                    <td className="text-slate-500">{d.label || d.type}</td>
                    <td>{d.status === "used"
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><Link2 className="h-3 w-3" /> bağlandı</span>
                      : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">rezerve</span>}</td>
                    <td className="text-[11px] text-slate-500">{d.ref_label || "—"}</td>
                    <td className="text-[11px] text-slate-400">{d.created_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {docNums.items.length > 0 && (
            <div className="mt-2"><Pagination page={dnPaged.page} pageSize={dnPaged.pageSize} total={dnPaged.total}
              onPage={dnPaged.setPage} onPageSize={dnPaged.setPageSize} testid="docnum-pagination" /></div>
          )}
        </div>
      </div>

      {/* Varlık Evrak Arşivi Arama (müşteri/tedarikçi/araç/personel) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="doc-entity-search-card">
        <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Search className="h-5 w-5 text-brand-green" /> Evrak Arşivi Arama</p>
        <p className="mt-1 text-sm text-slate-500">Tüm varlıklara (müşteri, tedarikçi, araç, personel) yüklenen evrakları tek yerden arayın — evrak adı, türü veya varlık adına göre.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="es-search" value={esQ} onChange={(e) => setEsQ(e.target.value)} placeholder="Evrak / varlık ara... (örn. sözleşme, ruhsat, Ahmet)" className="pl-9" />
          </div>
          <select data-testid="es-type" value={esType} onChange={(e) => setEsType(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm">
            <option value="">Tüm varlıklar</option>
            <option value="customer">Müşteri</option>
            <option value="supplier">Tedarikçi</option>
            <option value="vehicle">Araç</option>
            <option value="personnel">Personel</option>
          </select>
          <select data-testid="es-category" value={esCat} onChange={(e) => setEsCat(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm">
            <option value="">Tüm türler</option>
            {DOC_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <span data-testid="es-count" className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{esData.count || 0} sonuç</span>
        </div>
        <div className="mt-3 overflow-x-auto">
          {esLoading ? (
            <p className="py-6 text-center text-sm text-slate-400">Aranıyor...</p>
          ) : (esData.items || []).length === 0 ? (
            <p data-testid="es-empty" className="py-6 text-center text-sm text-slate-400">Eşleşen evrak yok. Farklı bir arama deneyin.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase text-slate-400">
                <th className="py-2">Varlık</th><th>Evrak</th><th>Tür</th><th>Bitiş</th><th></th>
              </tr></thead>
              <tbody>
                {(esData.items || []).map((d, i) => (
                  <tr key={d.doc_id || i} data-testid={`es-row-${d.doc_id || i}`} className="border-b border-slate-50">
                    <td className="py-2"><span className="mr-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{d.entity_label}</span>{d.entity_name}</td>
                    <td className="max-w-[220px] truncate font-medium text-slate-800">{d.doc_name || "—"}</td>
                    <td className="text-slate-500">{(DOC_CATEGORIES.find(([k]) => k === d.category) || [])[1] || d.category || "—"}</td>
                    <td className="text-[11px] text-slate-500">{d.expiry || "—"}</td>
                    <td className="text-right">{d.url ? <a data-testid={`es-open-${d.doc_id || i}`} href={fileUrl(d.url)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand-green hover:underline">aç</a> : <span className="text-[11px] text-slate-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {esData.truncated && <p className="mt-2 text-[11px] text-amber-600">İlk 300 sonuç gösteriliyor — aramayı daraltın.</p>}
        </div>
      </div>

      {/* Ayarlar — Saklama & GitHub (belirgin) */}
      {cfg && (
        <div className="rounded-2xl border-2 border-brand-green/40 bg-emerald-50/40 p-6 shadow-sm ring-1 ring-brand-green/10" data-testid="doc-retention-card">
          <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Github className="h-5 w-5 text-brand-green" /> GitHub Yedekleme & Saklama Arşivi</p>
          <p className="mt-1 text-sm text-slate-500">Belgeleriniz burada tanımladığınız süre kadar sistemde tutulur ve açtığınızda <b>kendi GitHub deponuza otomatik yedeklenir</b>. Her işletme KENDİ GitHub'ını bağlar; token'ınız yalnız sizin sisteminizde saklanır, kimseyle paylaşılmaz.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Sistemde Saklama Süresi (ay)</Label>
              <Input data-testid="doc-retention-months" type="number" min="1" value={cfg.months} onChange={(e) => setCfg({ ...cfg, months: e.target.value })} className="mt-1 w-32" />
              <p className="mt-1 text-[11px] text-slate-400">Bu süreden eski, korumasız belgeler arşive alınır. Yasal öneri: <b>120 ay (10 yıl)</b>.</p>
            </div>
            <div>
              <Label className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-green" /> Güvenlik Günlüğü Saklama (gün)</Label>
              <Input data-testid="doc-security-days" type="number" min="30" value={cfg.security_days ?? 180} onChange={(e) => setCfg({ ...cfg, security_days: e.target.value })} className="mt-1 w-32" />
              <p className="mt-1 text-[11px] text-slate-400">Bu süreden eski giriş/güvenlik olayları otomatik silinir (rotasyon). Başarısız & imkânsız-seyahat kayıtları 2× daha uzun tutulur. Varsayılan: <b>180 gün</b>.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
              <Label className="mb-0 flex items-center gap-2"><Github className="h-4 w-4" /> GitHub'a arşivle</Label>
              <Switch data-testid="doc-github-enabled" checked={cfg.github.enabled} onCheckedChange={(v) => setCfg({ ...cfg, github: { ...cfg.github, enabled: v } })} />
            </div>
          </div>
          {cfg.github.enabled && (
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2"><Label>Depo (owner/repo)</Label><Input data-testid="doc-github-repo" value={cfg.github.repo} onChange={(e) => setCfg({ ...cfg, github: { ...cfg.github, repo: e.target.value } })} placeholder="kullanici/arsiv-deposu" className="mt-1 font-mono" /></div>
              <div><Label>Dal (branch)</Label><Input data-testid="doc-github-branch" value={cfg.github.branch} onChange={(e) => setCfg({ ...cfg, github: { ...cfg.github, branch: e.target.value } })} className="mt-1 font-mono" /></div>
              <div className="sm:col-span-3"><Label>GitHub Token (repo yetkili PAT)</Label><Input data-testid="doc-github-token" type="password" value={cfg.github.token} onChange={(e) => setCfg({ ...cfg, github: { ...cfg.github, token: e.target.value } })} placeholder={cfg.github.configured ? "Kayıtlı — değiştirmek için yeni token girin" : "ghp_..."} className="mt-1 font-mono" /></div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="doc-retention-save" onClick={save} disabled={saving} className="bg-brand-green text-white hover:bg-brand-green-600">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Kaydet
            </Button>
            {cfg.github.enabled && (
              <Button data-testid="doc-github-test" onClick={testGithub} disabled={ghTesting} variant="outline" className="border-slate-300">
                {ghTesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Github className="mr-2 h-4 w-4" />} Bağlantıyı Test Et
              </Button>
            )}
            <Button data-testid="doc-retention-run" onClick={runNow} disabled={running} variant="outline">
              {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Şimdi Arşivle (tara)
            </Button>
          </div>
        </div>
      )}

      {/* Belge listesi */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="documents-list">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map(([v, l]) => (
              <button key={v} data-testid={`doc-filter-${v}`} onClick={() => setStatus(v)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${status === v ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
            ))}
          </div>
          {selected.size > 0 && (
            <Button data-testid="doc-bulk-delete" onClick={delBulk} disabled={deleting} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
              {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />} Seçili {selected.size} belgeyi sil
            </Button>
          )}
        </div>
        {docs.items.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Belge yok.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase text-slate-400">
                <th className="py-2 w-8">
                  <button type="button" data-testid="doc-select-all" onClick={togglePage} className="text-slate-400 hover:text-brand-green">
                    {allPageSel ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  </button>
                </th>
                <th className="py-2">Dosya</th><th>Tür</th><th>Boyut</th><th>Yüklenme</th><th>Durum</th><th></th>
              </tr></thead>
              <tbody>
                {docsPaged.slice.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`} className={`border-b border-slate-50 ${selected.has(d.id) ? "bg-red-50/40" : ""}`}>
                    <td className="py-2">
                      <button type="button" data-testid={`doc-select-${d.id}`} onClick={() => toggleSel(d.id)} className="text-slate-300 hover:text-brand-green">
                        {selected.has(d.id) ? <CheckSquare className="h-4 w-4 text-brand-green" /> : <Square className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="py-2 max-w-[220px] truncate font-medium text-slate-800">{d.filename || "—"}</td>
                    <td className="text-slate-500">{d.kind || "—"}</td>
                    <td className="text-slate-500">{fmtSize(d.size)}</td>
                    <td className="text-[11px] text-slate-500">{d.created_at ? fmtDate(d.created_at) : "—"}</td>
                    <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_META[d.status]?.cls || "bg-slate-100"}`}>{STATUS_META[d.status]?.label || d.status}</span></td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-3">
                        {(d.status === "archived" || d.status === "archive_ready") && (
                          <button data-testid={`doc-restore-${d.id}`} onClick={() => restoreDoc(d.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline"><RotateCcw className="h-3.5 w-3.5" /> geri getir</button>
                        )}
                        {d.github_url ? <a href={d.github_url} target="_blank" rel="noreferrer" data-testid={`doc-github-link-${d.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline"><Github className="h-3.5 w-3.5" /> arşiv</a>
                          : d.url ? <a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}?auth=${localStorage.getItem("groot_token") || ""}`} target="_blank" rel="noreferrer" data-testid={`doc-open-${d.id}`} className="text-xs font-semibold text-brand-green hover:underline">aç</a>
                            : <span className="text-[11px] text-slate-300">—</span>}
                        <button data-testid={`doc-delete-${d.id}`} onClick={() => delOne(d.id)} disabled={deleting} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2"><Pagination page={docsPaged.page} pageSize={docsPaged.pageSize} total={docsPaged.total}
              onPage={docsPaged.setPage} onPageSize={docsPaged.setPageSize} testid="documents-pagination" /></div>
          </div>
        )}
      </div>
    </div>
  );
}
